import { IconCheck, IconEdit, IconPaperclip, IconPlus, IconRefresh, IconTrash } from "@tabler/icons-react"
import { type FC, useEffect, useId, useMemo, useState } from "react"
import { useDropzone } from "react-dropzone"
import randomUUID from "uuid-random"

import { type EditableBokaderoCoursePage } from "@forento/core/models/bokadero"
import { type CoursePageInputType } from "@forento/core/models/course"
import { getDataUrlByFile, getExtensionByFileName } from "@forento/core/utilities/file"
import { toNullIfEmpty } from "@forento/core/utilities/string"
import { exhaustiveGuard } from "@forento/core/utilities/switch"
import {
	getLoomIdFromUrl,
	getLoomUrlFromId,
	getVimeoIdFromUrl,
	getVimeoUrlFromId,
	getYouTubeIdFromUrl,
	getYouTubeUrlFromId,
} from "@forento/core/utilities/url"
import Form from "@forento/ui/components/Form"
import { useFile } from "@forento/ui/hooks/useFile"
import { cn, tw } from "@forento/ui/utilities/tailwind"
import { startVideoUpload, type VideoUploadProgress } from "@forento/ui/utilities/video"

import Button, { ImageButton, SecondaryButton } from "~/components/button"
import { Input, Label, useRichTextArea } from "~/components/input"
import Modal from "~/components/modal"
import RichContentEditor from "~/components/rich-content-editor"
import {
	exportEditedRichContent,
	loadRichContent,
	parseRichContent,
	type RichContentValue,
} from "~/utilities/rich-content"
import { query } from "~/utilities/trpc"

type Props = {
	isOpen: boolean
	page: EditableBokaderoCoursePage
	reload(): Promise<unknown>
	onClose(): void
}

const EditModal: FC<Props> = ({ isOpen, page, reload, onClose }) => {
	const imageId = useId()
	const audioId = useId()
	const inputTypeId = useId()
	const videoId = useId()

	const [name, setName] = useState("")
	const text = useRichTextArea({ label: "Innehåll", initialValue: page.content, extendedFormatting: true })
	const [richContentValue, setRichContentValue] = useState<RichContentValue | null>(null)
	const [answers, setAnswers] = useState(page.answers)
	const [inputType, setInputType] = useState<CoursePageInputType>("single-line")
	const [bunnyVideoId, setBunnyVideoId] = useState(page.bunnyVideoId)
	const [youtubeUrl, setYouTubeUrl] = useState("")
	const [vimeoUrl, setVimeoUrl] = useState("")
	const [loomUrl, setLoomUrl] = useState("")
	const [addAttachments, setAddAttachments] = useState<
		{ tempId: string; label: string; base64: string; extension: string }[]
	>([])
	const [removeAttachments, setRemoveAttachments] = useState<string[]>([])
	const [addLinks, setAddLinks] = useState<{ tempId: string; label: string; url: string }[]>([])
	const [removeLinks, setRemoveLinks] = useState<string[]>([])

	const [videoUploadProgress, setVideoUploadProgress] = useState<VideoUploadProgress | null>(null)

	const imageFile = useFile(page.id, page.imageUrl)
	const audioFile = useFile(page.id, page.audioUrl)

	useEffect(() => {
		setName(page.name)
		setRichContentValue(page.intendedType === "custom" ? loadRichContent(page.content) : null)
		setInputType(page.inputType ?? "single-line")
		setBunnyVideoId(page.bunnyVideoId)
		setYouTubeUrl(getYouTubeUrlFromId(page.youTubeVideoId))
		setVimeoUrl(getVimeoUrlFromId(page.vimeoVideoId))
		setLoomUrl(getLoomUrlFromId(page.loomVideoId))
		setAddAttachments([])
		setAddLinks([])
		setRemoveAttachments([])
		setRemoveLinks([])
	}, [page])

	const updatePage = query.updatePage.useMutation({
		async onSuccess() {
			await reload()
			onClose()
		},
		onError: error => console.error(error),
	})
	const initializeVideoUpload = query.initializeVideoUpload.useMutation({
		onError: error => console.error(error),
	})
	const createVideo = query.createPageVideo.useMutation({
		onSuccess: ({ bunnyVideoId }) => setBunnyVideoId(bunnyVideoId),
		onError: error => console.error(error),
	})
	const deleteVideo = query.deletePageVideo.useMutation({
		onSuccess() {
			setBunnyVideoId(null)
			setVideoUploadProgress(null)
		},
		onError: error => console.error(error),
	})

	const imageDropzone = useDropzone({
		accept: { "image/*": [".png", ".jpg", ".jpeg"] },
		onDrop(files) {
			const file = files[0]
			if (file === undefined) return
			imageFile.imageUploader.onUpload(file)
		},
	})
	const videoDropzone = useDropzone({
		accept: { "video/*": [] },
		async onDrop(files) {
			const file = files[0]
			if (file === undefined) return

			const { bunnyVideoId } = await createVideo.mutateAsync(page.id)
			const videoUpload = await initializeVideoUpload.mutateAsync({ bunnyVideoId })
			await startVideoUpload({ file, bunnyVideoId, ...videoUpload, onProgress: setVideoUploadProgress })
		},
	})
	const audioDropzone = useDropzone({
		accept: { "audio/*": [".mp3", ".wav", ".m4a"] },
		async onDrop(files) {
			const file = files[0]
			if (file === undefined) return
			audioFile.onChange(file)
		},
	})
	const attachmentDropzone = useDropzone({
		async onDrop(files) {
			const file = files[0]
			if (file === undefined) return

			const result = prompt("Ange bilagans namn")
			if (result === null) return

			const extension = getExtensionByFileName(file.name)
			if (extension === null) return

			const base64 = await getDataUrlByFile(file)

			setAddAttachments(current => [
				...current,
				{ tempId: randomUUID(), label: toNullIfEmpty(result) ?? "Namnlös bilaga", base64, extension },
			])
		},
	})

	function handleAddLink() {
		const url = prompt("Ange länken")
		if (url === null || url.trim().length === 0) return

		const label = prompt("Ange länkens namn")
		if (label === null) return

		setAddLinks(current => [
			...current,
			{ tempId: randomUUID(), label: toNullIfEmpty(label) ?? "Namnlös länk", url },
		])
	}

	async function handleSubmit() {
		updatePage.mutate({
			id: page.id,
			name,
			content:
				page.intendedType === "custom" ? exportEditedRichContent(richContentValue) : text.exportEditorState(),
			imageBase64: imageFile.isModified ? await imageFile.exportToDataUrl() : undefined,
			youTubeVideoId: getYouTubeIdFromUrl(youtubeUrl),
			vimeoVideoId: getVimeoIdFromUrl(vimeoUrl),
			loomVideoId: getLoomIdFromUrl(loomUrl),
			audioBase64: audioFile.isModified ? await audioFile.exportToDataUrl() : undefined,
			inputType,
			answers,
			addAttachments,
			removeAttachments,
			addLinks,
			removeLinks,
		})
	}

	const initialRichContentValue = useMemo(() => {
		return page.intendedType === "custom" ? parseRichContent(page.content) : null
	}, [page.intendedType, page.content])

	const inputOptions: { id: CoursePageInputType; label: string }[] = [
		{ id: "single-line", label: "En rad" },
		{ id: "multi-line", label: "Flera rader" },
	]
	const imageContainerClasses = tw`flex aspect-video w-sm cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50`
	const deleteVideoButtonClasses = tw`cursor-pointer rounded-full bg-red-600/90 px-3 py-3 text-white shadow transition-colors hover:bg-red-700`

	return (
		<Modal isOpen={isOpen}>
			<Form onSubmit={handleSubmit}>
				<h1 className="mb-4 text-xl font-semibold text-slate-800">Redigera sida</h1>

				<div className="mb-4 flex flex-col gap-4">
					<Input
						type="text"
						label="Titel"
						value={name}
						onChange={setName}
						placeholder="Ange sidans titel"
						required
					/>
					{page.intendedType !== "custom" && (
						<>
							{text.element}
							{(page.intendedType === "text" ||
								page.intendedType === "audio" ||
								page.intendedType === "input" ||
								page.intendedType === "quiz") && (
								<div>
									<Label forId={imageId}>Bild</Label>
									{imageFile.filePath ? (
										<div className="group relative self-start">
											<input {...imageDropzone.getInputProps()} id={imageId} />
											<img
												src={imageFile.filePath}
												alt="Bild"
												className="aspect-video w-64 rounded-lg border-2 border-slate-200 object-cover shadow-sm"
											/>
											<div className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg bg-black/0 transition-colors group-hover:bg-black/20">
												<ImageButton onClick={imageDropzone.open}>
													<IconEdit className="size-3.5" /> Ändra
												</ImageButton>
												<ImageButton onClick={imageFile.clear} danger>
													<IconTrash className="size-3.5" /> Ta bort
												</ImageButton>
											</div>
										</div>
									) : (
										<div className={imageContainerClasses} {...imageDropzone.getRootProps()}>
											<input {...imageDropzone.getInputProps()} id={imageId} />
											<div className="text-center text-sm text-slate-500">Lägg till bild</div>
										</div>
									)}
								</div>
							)}
						</>
					)}
					{page.intendedType === "quiz" && (
						<div>
							<Label>Frågor</Label>
							<div className="flex flex-col gap-3">
								{answers.map(answer => (
									<div key={answer.id} className="flex items-center gap-3">
										<Input
											type="text"
											placeholder="Ange svar"
											value={answer.text}
											onChange={text =>
												setAnswers(answers.map(a => (a.id === answer.id ? { ...a, text } : a)))
											}
											required
											className="w-full"
										/>
										<label className="flex cursor-pointer items-center">
											<input
												type="checkbox"
												checked={answer.isCorrect}
												onChange={event =>
													setAnswers(
														answers.map(a =>
															a.id === answer.id
																? { ...a, isCorrect: event.target.checked }
																: a,
														),
													)
												}
												className="peer sr-only"
											/>
											<div className="flex h-6 w-6 items-center justify-center rounded border border-slate-300 bg-white transition-colors">
												<IconCheck
													className={cn(
														"text-primary size-4 transition-transform",
														answer.isCorrect ? "scale-100" : "scale-0",
													)}
												/>
											</div>
										</label>
										<button
											type="button"
											onClick={() => setAnswers(answers.filter(a => a.id !== answer.id))}
											className="cursor-pointer text-red-600 transition-colors hover:text-red-500"
										>
											<IconTrash className="size-6" />
										</button>
									</div>
								))}
								<Button
									type="button"
									color="green"
									onClick={() =>
										setAnswers([
											...answers,
											{ id: crypto.randomUUID(), text: "", isCorrect: false },
										])
									}
									className="self-start"
								>
									<IconPlus className="size-3.5" /> Lägg till svar
								</Button>
							</div>
						</div>
					)}
					{page.intendedType === "input" && (
						<div>
							<Label forId={inputTypeId}>Inmatningstyp</Label>
							<select
								id={inputTypeId}
								value={inputType}
								onChange={e => setInputType(e.target.value as CoursePageInputType)}
								className="focus:border-primary focus:ring-primary w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-500 focus:ring-1 focus:outline-none"
							>
								{inputOptions.map(option => (
									<option key={option.id} value={option.id}>
										{option.label}
									</option>
								))}
							</select>
						</div>
					)}
					{page.intendedType === "video" && (
						<div>
							<Label forId={videoId}>Video</Label>
							{videoUploadProgress === null || videoUploadProgress.status === "completed" ? (
								<div>
									{bunnyVideoId ? (
										<div className="flex items-center gap-4">
											<div className={imageContainerClasses}>
												<iframe
													src={`/api/bunny/iframe/${bunnyVideoId}`}
													allow="accelerometer; gyroscope; encrypted-media; picture-in-picture;"
													allowFullScreen
													className="h-full w-full rounded-lg border-none"
												/>
											</div>
											<button
												type="button"
												onClick={() => deleteVideo.mutate(page.id)}
												className={deleteVideoButtonClasses}
											>
												<IconTrash className="size-5" />
											</button>
										</div>
									) : (
										<div className={imageContainerClasses} {...videoDropzone.getRootProps()}>
											<input {...videoDropzone.getInputProps()} id={videoId} />
											<div className="text-center text-sm text-slate-500">Lägg till video</div>
										</div>
									)}
								</div>
							) : videoUploadProgress.status === "uploading" ? (
								<div className={cn(imageContainerClasses, "flex-col gap-2 p-4")}>
									<div className="h-2 w-full rounded-full bg-slate-200">
										<div
											className="bg-primary h-full rounded-full transition-all"
											style={{ width: `${videoUploadProgress.progress * 100}%` }}
										/>
									</div>
									<div className="text-sm text-slate-500">
										{Math.floor(videoUploadProgress.progress * 100)}%
									</div>
								</div>
							) : videoUploadProgress.status === "failed" ? (
								<div className="flex items-center gap-4">
									<div className={cn(imageContainerClasses, "p-4")}>
										<div className="text-center text-sm text-slate-500">
											Video upload misslyckades. Försök igen.
										</div>
									</div>
									<button
										type="button"
										onClick={() => deleteVideo.mutate(page.id)}
										className={deleteVideoButtonClasses}
									>
										<IconRefresh className="size-5" />
									</button>
								</div>
							) : (
								exhaustiveGuard(videoUploadProgress.status)
							)}
						</div>
					)}
					{page.intendedType === "youtube" && (
						<Input
							type="text"
							label="YouTube-länk"
							value={youtubeUrl}
							onChange={setYouTubeUrl}
							placeholder="Ange YouTube-länk"
							required
						/>
					)}
					{page.intendedType === "vimeo" && (
						<Input
							type="text"
							label="Vimeo-länk"
							value={vimeoUrl}
							onChange={setVimeoUrl}
							placeholder="Ange Vimeo-länk"
							required
						/>
					)}
					{page.intendedType === "loom" && (
						<Input
							type="text"
							label="Loom-länk"
							value={loomUrl}
							onChange={setLoomUrl}
							placeholder="Ange Loom-länk"
							required
						/>
					)}
					{page.intendedType === "custom" && (
						<RichContentEditor
							placeholder="Ange innehåll"
							initialValue={initialRichContentValue}
							setValue={setRichContentValue}
						/>
					)}

					{page.intendedType === "audio" && (
						<div>
							<Label forId={audioId}>Ljud</Label>
							{audioFile.filePath ? (
								<div className="self-start">
									<input {...audioDropzone.getInputProps()} id={audioId} />
									<audio controls src={audioFile.filePath} className="mb-4 w-full max-w-md" />
									<div className="flex items-center gap-4">
										<Button color="purple" onClick={audioDropzone.open}>
											<IconEdit className="size-3.5" /> Ändra
										</Button>
										<Button color="red" onClick={audioFile.clear}>
											<IconTrash className="size-3.5" /> Ta bort
										</Button>
									</div>
								</div>
							) : (
								<div
									className="flex h-24 w-sm cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50"
									{...audioDropzone.getRootProps()}
								>
									<input {...audioDropzone.getInputProps()} id={audioId} />
									<div className="text-center text-sm text-slate-500">Lägg till ljud</div>
								</div>
							)}
						</div>
					)}

					<div>
						<Label>Bilagor</Label>
						<div className="flex flex-col gap-3">
							{page.attachments
								.filter(attachment => !removeAttachments.includes(attachment.id))
								.map(attachment => (
									<Attachment
										key={attachment.id}
										id={attachment.id}
										label={attachment.label}
										onDelete={() => setRemoveAttachments([...removeAttachments, attachment.id])}
									/>
								))}
							{addAttachments.map(attachment => (
								<Attachment
									key={attachment.tempId}
									id={attachment.tempId}
									label={attachment.label}
									onDelete={() =>
										setAddAttachments(addAttachments.filter(x => x.tempId !== attachment.tempId))
									}
								/>
							))}
							<div>
								<input {...attachmentDropzone.getInputProps()} />
								<Button color="green" onClick={attachmentDropzone.open} className="self-start">
									<IconPlus className="size-3.5" /> Lägg till bilaga
								</Button>
							</div>
						</div>
					</div>
					<div>
						<Label>Länkar</Label>
						<div className="flex flex-col gap-3">
							{page.links
								.filter(link => !removeLinks.includes(link.id))
								.map(link => (
									<Attachment
										key={link.id}
										id={link.id}
										label={link.label}
										onDelete={() => setRemoveLinks([...removeLinks, link.id])}
									/>
								))}
							{addLinks.map(link => (
								<Attachment
									key={link.tempId}
									id={link.tempId}
									label={link.label}
									onDelete={() => setAddLinks(addLinks.filter(x => x.tempId !== link.tempId))}
								/>
							))}
							<Button color="green" onClick={handleAddLink} className="self-start">
								<IconPlus className="size-3.5" /> Lägg till länk
							</Button>
						</div>
					</div>
				</div>

				<div className="flex justify-end gap-4">
					<SecondaryButton onClick={onClose}>Avbryt</SecondaryButton>
					<Button type="submit" color="purple" disabled={updatePage.isPending}>
						Spara
					</Button>
				</div>
			</Form>
		</Modal>
	)
}

const Attachment: FC<{ id: string; label: string; onDelete(): void }> = ({ id, label, onDelete }) => (
	<div
		key={id}
		className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 shadow-sm"
	>
		<div className="flex items-center gap-2">
			<IconPaperclip className="size-4 text-gray-400" />
			<span className="text-sm font-medium text-gray-800">{label}</span>
		</div>
		<button
			type="button"
			className="cursor-pointer rounded-full p-1 transition-colors hover:bg-red-100"
			aria-label="Ta bort bilaga"
			onClick={onDelete}
		>
			<IconTrash className="size-5 text-red-500" />
		</button>
	</div>
)

export default EditModal
